import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Space,
  message,
  Alert as AntdAlert,
  Radio,
  Input,
} from 'antd';
import { useCancelSubscription } from '../../hooks/payment';
import { CANCEL_SUBSCRIPTION_MODAL } from '../../utils/text';
import styled from '@emotion/styled';

const { TextArea } = Input;

const UL = styled.ul`
  padding-left: 25px;
  margin: 0;

  li {
    color: var(--text-secondary);
  }
`;

const Alert = styled.p`
  color: var(--text-secondary);
  margin-top: 16px;
  margin-bottom: 0;
  font-size: var(--text-size-sm);
  font-weight: var(--text-weight-regular);
  line-height: var(--line-height-base);
`;

const Description = styled.p`
  color: var(--text-secondary);
  margin-top: 16px;
  margin-bottom: 0;
  font-size: var(--text-size-sm);
  font-weight: var(--text-weight-regular);
  line-height: var(--line-height-base);
`;

const ReasonStatement = styled.p`
  color: var(--text-primary);
  margin-top: 0;
  margin-bottom: 20px;
  font-size: var(--text-size-sm);
  font-weight: var(--text-weight-regular);
  line-height: var(--line-height-base);
`;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const StyledTextArea = styled(TextArea)`
  margin-top: 12px;
`;

interface CancelSubscriptionModalProps {
  visible: boolean;
  loading?: boolean;
  onCancel: () => void;
  siteId: string;
  refetch: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  visible,
  loading = false,
  onCancel,
  siteId,
  refetch,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReasonText, setOtherReasonText] = useState<string>('');

  const {
    mutate: cancelSubscriptionMutation,
    isPending,
    isSuccess,
    isError,
  } = useCancelSubscription();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isSuccess) {
      messageApi.success('Subscription cancelled successfully');
      // Reset state
      setStep(1);
      setSelectedReason('');
      setOtherReasonText('');
      onCancel();
      refetch();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setError('Failed to cancel subscription, Contact support.');
      messageApi.error('Failed to cancel subscription. Contact support.');
    }
  }, [isError]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setStep(1);
      setSelectedReason('');
      setOtherReasonText('');
      setError(null);
    }
  }, [visible]);

  const handleProceedToReason = () => {
    setStep(2);
    setError(null);
  };

  const handleCancelSubscription = () => {
    const reason =
      selectedReason === CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER
        ? otherReasonText.trim()
        : selectedReason;

    if (!reason) {
      messageApi.warning('Please select a reason for cancellation');
      return;
    }

    setError(null);
    cancelSubscriptionMutation({ siteId, reason });
  };

  const isOtherSelected =
    selectedReason === CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER;
  const isCancelDisabled =
    !selectedReason || (isOtherSelected && !otherReasonText.trim());

  return (
    <Modal
      title={
        step === 1
          ? CANCEL_SUBSCRIPTION_MODAL.TITLE
          : CANCEL_SUBSCRIPTION_MODAL.REASON_TITLE
      }
      visible={visible}
      onCancel={onCancel}
      centered
      footer={null}
      destroyOnClose
      width={600}
    >
      {contextHolder}

      {step === 1 ? (
        <>
          <Description>
            Your current plan will remain active until the end of your billing
            period, and you won't be charged again.
          </Description>

          <Description>After that:</Description>
          <UL>
            <li>Your property will move to the Free tier</li>
            <li>
              You will lose access to all Premium features, including advanced
              customizations, Consent or Pay, and more.
            </li>
            <li>
              You can re-upgrade anytime — your previous settings will be
              retained.
            </li>
          </UL>

          <Alert>❗ This action cannot be undone.</Alert>

          <Space
            style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}
          >
            <Button onClick={onCancel} type="primary">
              {CANCEL_SUBSCRIPTION_MODAL.KEEP_PREMIUM}
            </Button>
            <Button type="primary">
              {CANCEL_SUBSCRIPTION_MODAL.INTEGRATE_WITH_SSP}
            </Button>
            <Button type="default" onClick={handleProceedToReason}>
              {CANCEL_SUBSCRIPTION_MODAL.YES_BUTTON}
            </Button>
          </Space>
        </>
      ) : (
        <>
          <ReasonStatement>
            {CANCEL_SUBSCRIPTION_MODAL.REASON_STATEMENT}
          </ReasonStatement>

          <RadioGroup
            value={selectedReason}
            onChange={(e) => {
              setSelectedReason(e.target.value);
              // Clear other reason text if not "Other"
              if (e.target.value !== CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER) {
                setOtherReasonText('');
              }
            }}
          >
            <Radio value={CANCEL_SUBSCRIPTION_MODAL.REASON_INTEGRATED_SSP}>
              {CANCEL_SUBSCRIPTION_MODAL.REASON_INTEGRATED_SSP}
            </Radio>
            <Radio value={CANCEL_SUBSCRIPTION_MODAL.REASON_PRICING}>
              {CANCEL_SUBSCRIPTION_MODAL.REASON_PRICING}
            </Radio>
            <Radio value={CANCEL_SUBSCRIPTION_MODAL.REASON_MOVING_CMP}>
              {CANCEL_SUBSCRIPTION_MODAL.REASON_MOVING_CMP}
            </Radio>
            <Radio value={CANCEL_SUBSCRIPTION_MODAL.REASON_INACTIVE}>
              {CANCEL_SUBSCRIPTION_MODAL.REASON_INACTIVE}
            </Radio>
            <Radio value={CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER}>
              {CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER}
            </Radio>
          </RadioGroup>

          {isOtherSelected && (
            <StyledTextArea
              rows={4}
              maxLength={127}
              showCount
              placeholder={CANCEL_SUBSCRIPTION_MODAL.REASON_OTHER_PLACEHOLDER}
              value={otherReasonText}
              onChange={(e) => setOtherReasonText(e.target.value)}
            />
          )}

          <div
            style={{ width: '100%', justifyContent: 'flex-end', marginTop: 20, display: 'flex', gap: 10 }}
          >
            <Button onClick={onCancel}>
              {CANCEL_SUBSCRIPTION_MODAL.DISMISS_BUTTON}
            </Button>
            <Button
              type="default"
              loading={isPending}
              disabled={isCancelDisabled}
              onClick={handleCancelSubscription}
            >
              {CANCEL_SUBSCRIPTION_MODAL.CANCEL_SUBSCRIPTION_BUTTON}
            </Button>
          </div>

          {error && (
            <AntdAlert type="error" message={error} style={{ marginTop: 8 }} />
          )}
        </>
      )}
    </Modal>
  );
};

export default CancelSubscriptionModal;
